import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import {
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    ChakraProvider,
    Box,
    Button,
    Text,
    Spinner,
    Stack,
    Flex,
    Icon,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react'
import { GeneralSearchIcon } from '../icons'
import FindInStoreList from '../../components/find-in-store-list'
import { useFindInStore } from '../../hooks/use-find-in-store'

/* Styles */
import { StoreLocatorStylesProvider } from '../store-locator/styles/storeLocatorStyles'

/* Providers */
import { StoreLocatorProvider } from '../../contexts/store-locator-context'
import { APIProvider } from '@vis.gl/react-google-maps';

/* Components */
import StoreLocatorMap from '../store-locator/partials/store-locator-map'
import StoreLocatorInfo from '../store-locator/partials/store-locator-info'
import StoreDetailView from './partial/storeDetailView'

/* Configs */
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { STORE_LOCATOR_CONFIGS } from '../store-locator/configs'

/* Context */
import { useStoreLocator } from '../../contexts/store-locator-context';

/* Utils */
import { storeModel } from '../../utils/find-in-store-utils'

const FindInStoreContent = ({
    product,
    masterProduct,
    setFindInStoreModalTitle,
    productName,
    onFindInStoreClose
}) => {
    const slStyles = useMultiStyleConfig('StoreLocator', {})
    const intl = useIntl()
    const isDesktop = useBreakpointValue({ base: false, md: true })
    const isMobile = useBreakpointValue({ base: true, md: false })
    setFindInStoreModalTitle(
        intl.formatMessage({ id: 'pdp_find_in_store.title', defaultMessage: 'find a store' })
    )

    const [permissionStatus, setPermissionStatus] = useState(null)
    const [loading, setLoading] = useState(true)
    const [address, setAddress] = useState('')
    const [latitude, setLatitude] = useState('')
    const [longitude, setLongitude] = useState('')
    const [hasSearched, setHasSearched] = useState(false)
    const [showStoreList, setShowStoreList] = useState(false)
    const [selectedStore, setSelectedStore] = useState(null)
    const [availabilityText, setAvailabilityText] = useState(null)
    const [availabilityColor, setAvailabilityColor] = useState(null)

    const price = product?.price
    const variationValues = product?.variationValues || {}
    const productId = product?.productId

    // Resolve variation names from master product
    const getVariationName = (attributeId, value) => {
        const attribute = masterProduct?.variationAttributes?.find(attr => attr.id === attributeId)
        const variationValue = attribute?.values?.find(val => val.value === value)
        return variationValue?.name || value
    }

    const colorName = getVariationName('color', variationValues?.color)
    const sizeName = getVariationName('size', variationValues?.size)

    const { findInStoreData, isFindInStoreLoading, fetchStores } = useFindInStore({
        productId,
        address,
        lat: latitude,
        lng: longitude,
        geoPermission: permissionStatus == 'granted'
    })

    const {
        map,
        setStores,
        selectedStore: mapSelectedStore
    } = useStoreLocator()

    const handleSearch = () => {
        setLatitude('')
        setLongitude('')
        setHasSearched(true)
        setShowStoreList(true)
        fetchStores({ productId, address })
    }

    const handleOnStoreInfoClick = (detailViewStore) => {
        if (findInStoreData?.stores && detailViewStore) {
            setSelectedStore(findInStoreData?.stores?.find((store) => store?.store_id === detailViewStore?.id))
        }
    }

    const handleSelectedStore = (store) => {
        setSelectedStore(store)
    }

    const getLocation = () => {
        if (!navigator.geolocation) {
            setLoading(false)
            return
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLoading(false)
                setHasSearched(true)
                setShowStoreList(true)
                setLatitude(position.coords.latitude.toString()) //('45.5114')
                setLongitude(position.coords.longitude.toString()) //('-73.67')
            },
            () => {
                setLoading(false)
            }
        );
    };

    const getAvailabilityInfo = (inventory) => {
        if (inventory > 2) {
            return {
                text: intl.formatMessage(
                    {
                        id: 'pdp_find_in_store.available',
                        defaultMessage: 'Available ({inventory})'
                    },
                    { inventory }
                ),
                color: 'successText'
            }
        } else if (inventory > 0) {
            return {
                text: intl.formatMessage(
                    {
                        id: 'pdp_find_in_store.low',
                        defaultMessage: 'Low Stock ({inventory})'
                    },
                    { inventory }
                ),
                color: 'warningText'
            }
        } else {
            return {
                text: intl.formatMessage({
                    id: 'pdp_find_in_store.oos',
                    defaultMessage: 'Out of Stock'
                }),
                color: 'dangerText'
            }
        }
    }

    useEffect(() => {
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then((status) => {
                setPermissionStatus(status.state)

                if (status.state === 'granted') {
                    getLocation()
                } else {
                    setLoading(false)
                }

                status.onchange = () => {
                    setPermissionStatus(status.state)
                    if (status.state === 'granted') {
                        getLocation()
                    }
                };
            });
        } else {
            setPermissionStatus('prompt')
            setLoading(false)
        }
    }, []);

    useEffect(() => {
        if (latitude && longitude) {
            fetchStores({ productId, lat: latitude, lng: longitude })
        }
    }, [latitude, longitude])

    useEffect(() => {
        if (findInStoreData?.stores) {
            setStores(storeModel(findInStoreData?.stores))
        }
    }, [findInStoreData])

    useEffect(() => {
        if (!findInStoreData?.stores && map) {
            setStores([])
            map.panTo({ lat: STORE_LOCATOR_CONFIGS.defaultCenter.latitude, lng: STORE_LOCATOR_CONFIGS.defaultCenter.longitude })
            map.setZoom(STORE_LOCATOR_CONFIGS.initialZoom)
        }
    }, [findInStoreData, map])

    useEffect(() => {

        if (selectedStore) {
            const { text, color } = getAvailabilityInfo(selectedStore?.inventory)
            setAvailabilityColor(color)
            setAvailabilityText(text)
        }

        if (mapSelectedStore) {
            const findInStoreModel = findInStoreData?.stores?.find((store) => store?.store_id === mapSelectedStore?.id)
            const { text, color } = getAvailabilityInfo(findInStoreModel?.inventory)

            setAvailabilityColor(color)
            setAvailabilityText(text)
        }
    }, [selectedStore, mapSelectedStore])

    return (
        <Flex>
            {isDesktop &&
                <Box flex={1}>
                    <StoreLocatorMap />
                </Box>
            }
            <Box
                maxWidth={{ md: "483px" }}
                w={'100%'}
                p={{ base: '84px 16px 0', md: '100px 16px 24px' }}
                overflow={{ md: 'auto' }}
                height={{ md: 'calc(100vh)' }}>
                <Box mb={12} px={{md: 2}}>
                    <Text fontWeight="medium" mb={1}>
                        {productName}
                    </Text>

                    <Stack spacing={0.5} fontSize="sm">
                        <Text>
                            <FormattedMessage
                                defaultMessage="Colour: {color}"
                                id="pdp_find_in_store.color"
                                values={{ color: colorName }}
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                defaultMessage="Size: {size}"
                                id="pdp_find_in_store.size"
                                values={{ size: sizeName }}
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                defaultMessage="Price: {price}"
                                id="pdp_find_in_store.price"
                                values={{
                                    price: intl.formatNumber(Number(price), {
                                        style: 'currency',
                                        currency: 'GBP',
                                        maximumFractionDigits: 2,
                                        useGrouping: false
                                    })
                                }}
                            />
                        </Text>
                    </Stack>
                </Box>
                <InputGroup px={{md: 2}}>
                    <InputLeftElement width="auto" top="-9px">
                        <IconButton
                            aria-label="Search"
                            icon={<Icon as={GeneralSearchIcon} />}
                            variant="ghost"
                            size="sm"
                            onClick={handleSearch}
                            _hover={{ bg: 'transparent' }}
                            color="black"
                        />
                    </InputLeftElement>

                    <Input
                        pb="8px"
                        pl="3rem"
                        variant="unstyled"
                        borderBottom="1px solid"
                        borderColor="black"
                        placeholder={intl.formatMessage({
                            id: 'pdp_find_in_store.search.placeholder',
                            defaultMessage: 'Search city, address or zip code'
                        })}
                        borderRadius={0}
                        _placeholder={{ color: 'gray.700' }}
                        onChange={(e) => setAddress(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                </InputGroup>
                {findInStoreData && findInStoreData.stores && (
                    <Text mt={2.5} fontSize="12px">
                        <FormattedMessage
                            defaultMessage="{storesQty} stores within {distance} km"
                            id="findInStoreData.stores.length"
                            values={{
                                storesQty: findInStoreData.stores.length,
                                distance: findInStoreData.maxDistance
                            }}
                        />
                    </Text>
                )}
                <ChakraProvider>
                    <Box>
                        {loading ? (
                            <Spinner />
                        ) : (
                            <>
                                {permissionStatus !== 'denied' && !hasSearched && (
                                    <Flex flexDirection="column" gap={4} py={12}>
                                        <Text fontSize="lg" fontWeight="bold" textTransform="uppercase">
                                            <FormattedMessage
                                                defaultMessage="Your Location services are turned off"
                                                id="pdp_find_in_store.location.off"
                                            />
                                        </Text>
                                        <Text fontSize="md" letterSpacing={0}>
                                            <FormattedMessage
                                                defaultMessage="Turn on Location Services in your device Settings to search for your stores by current location. You can still search by Country/Region, City or Zip Code."
                                                id="pdp_find_in_store.location.off.description"
                                            />
                                        </Text>
                                        <Button
                                            backgroundColor="black"
                                            borderRadius={0}
                                            onClick={getLocation}
                                            color="white"
                                            width="100%"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            textTransform="uppercase"
                                        >
                                            <FormattedMessage
                                                defaultMessage="Allow Location"
                                                id="pdp_find_in_store.button.allow.location"
                                            />
                                        </Button>
                                    </Flex>
                                )}
                            </>
                        )}
                    </Box>
                </ChakraProvider>
                {showStoreList && (
                    <>
                        {isMobile &&
                            <Tabs
                                borderColor={'gray.50'}
                                margin={{ base: '43px -16px 0' }}>
                                <TabList>
                                    <Tab {...slStyles.tab}>
                                        <FormattedMessage
                                            id="store_locator.tab.list"
                                            defaultMessage="List"
                                        />
                                    </Tab>
                                    <Tab {...slStyles.tab}>
                                        <FormattedMessage
                                            id="store_locator.tab.map"
                                            defaultMessage="Map"
                                        />
                                    </Tab>
                                </TabList>

                                <TabPanels>
                                    <TabPanel {...slStyles.storesTabPanel} p={'0 8px'}>
                                        <FindInStoreList
                                            stores={findInStoreData?.stores}
                                            isFindInStoreLoading={isFindInStoreLoading}
                                            onSelectedStore={handleSelectedStore}
                                        />
                                    </TabPanel>
                                    <TabPanel {...slStyles.storesTabPanel} paddingBottom={0}>
                                        <StoreLocatorMap />
                                        <StoreLocatorInfo variant='FindInStore'
                                            onStoreInfoClick={handleOnStoreInfoClick}
                                            availabilityColor={availabilityColor}
                                            availabilityText={availabilityText} />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>}

                        {isDesktop &&
                            <FindInStoreList
                                stores={findInStoreData?.stores}
                                isFindInStoreLoading={isFindInStoreLoading}
                                onSelectedStore={handleSelectedStore}
                            />}
                    </>
                )}

                <StoreDetailView stores={findInStoreData?.stores}
                    onFindInStoreClose={onFindInStoreClose}
                    selectedStore={selectedStore}
                    setSelectedStore={setSelectedStore}
                    availabilityColor={availabilityColor}
                    availabilityText={availabilityText} />
            </Box>
        </Flex>
    );
}

FindInStoreContent.propTypes = {
    product: PropTypes.object,
    masterProduct: PropTypes.object,
    setFindInStoreModalTitle: PropTypes.func,
    productName: PropTypes.string,
    onFindInStoreClose: PropTypes.func
}

const FindInStore = ({
    product,
    masterProduct,
    setFindInStoreModalTitle,
    productName,
    onFindInStoreClose
}) => {
    const storeLocatorStyling = useMultiStyleConfig('StoreLocator')
    const config = getConfig()
    const GOOGLE_MAPS_API_KEY = config.app.GOOGLE_MAPS_API_KEY || null
    return (
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <StoreLocatorProvider>
                <StoreLocatorStylesProvider value={storeLocatorStyling}>
                    <FindInStoreContent
                        product={product}
                        masterProduct={masterProduct}
                        setFindInStoreModalTitle={setFindInStoreModalTitle}
                        productName={productName}
                        onFindInStoreClose={onFindInStoreClose} />
                </StoreLocatorStylesProvider>
            </StoreLocatorProvider>
        </APIProvider>
    )
}

FindInStore.propTypes = {
    product: PropTypes.object,
    masterProduct: PropTypes.object,
    setFindInStoreModalTitle: PropTypes.func,
    productName: PropTypes.string,
    onFindInStoreClose: PropTypes.func
}

export default FindInStore
